"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useLanguage } from "../context/LanguageContext"
import LanguageSelector from "../components/LanguageSelector"
import { ref, push, set } from "firebase/database"
import { database } from "../config/firebase"
import emailjs from "@emailjs/browser"
import "./landing.css"

const Landing = () => {
  const { language } = useLanguage()

  const [activeTab, setActiveTab] = useState("padres")
  const [activeFaq, setActiveFaq] = useState(null)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [testimonalIndex, setTestimonialIndex] = useState(0)
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" })
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  // Captación B2B (escuelas/instituciones) → nodo leads
  const [leadForm, setLeadForm] = useState({ institution: "", contactName: "", email: "", phone: "", numTeachers: "", numStudents: "", message: "" })
  const [leadSubmitting, setLeadSubmitting] = useState(false)
  const [leadStatus, setLeadStatus] = useState(null)

  const handleLeadSubmit = async (e) => {
    e.preventDefault()
    setLeadSubmitting(true)
    setLeadStatus(null)
    try {
      const leadRef = push(ref(database, "leads"))
      await set(leadRef, {
        institution: leadForm.institution,
        contactName: leadForm.contactName,
        email: leadForm.email,
        phone: leadForm.phone || null,
        numTeachers: leadForm.numTeachers ? Number(leadForm.numTeachers) : null,
        numStudents: leadForm.numStudents ? Number(leadForm.numStudents) : null,
        message: leadForm.message || null,
        country: "VE",
        status: "new",
        createdAt: Date.now(),
      })
      setLeadStatus("success")
      setLeadForm({ institution: "", contactName: "", email: "", phone: "", numTeachers: "", numStudents: "", message: "" })
    } catch (error) {
      console.error("Error in lead submission:", error)
      setLeadStatus("error")
    } finally {
      setLeadSubmitting(false)
    }
  }

  const handleContactSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitStatus(null)

    try {
      // 1. Save to Firebase (Backup)
      const messagesRef = ref(database, "contact_messages")
      const newMessageRef = push(messagesRef)
      await set(newMessageRef, {
        ...contactForm,
        timestamp: Date.now(),
        status: "unread"
      })
      
      // 2. Send via EmailJS
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

      if (serviceId && templateId && publicKey) {
        await emailjs.send(
          serviceId,
          templateId,
          {
            from_name: contactForm.name,
            from_email: contactForm.email,
            message: contactForm.message,
            reply_to: contactForm.email,
          },
          publicKey
        )
      }

      setSubmitStatus("success")
      //console.log("Message successfully saved and sent:", contactForm)
      setContactForm({ name: "", email: "", message: "" })
    } catch (error) {
      console.error("Error in contact submission:", error)
      setSubmitStatus("error")
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev + 1) % 5)
  }

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev - 1 + 5) % 5)
  }

  const translations = {
    es: {
      nav: { login: "Acceder", contact: "Contáctanos", banner: "Personalización familiar + IA + Método pedagógico comprobado = Resultados en 90 días. Aprender más.", problem: "Características", features: "Funcionalidades", howItWorks: "Cómo Funciona", testimonials: "Testimonios", pricing: "Precios", faq: "FAQ", freeTrial: "Prueba Gratis 7 Días" },
      hero: {
        title: "La única app donde tu hijo aprende a leer",
        highlight: " viendo a las personas que ama.",
        tabParents: "Para Padres",
        tabSchools: "Para Escuelas",
        tabOrgs: "ONGs & Gobierno",
        cardTitle: "es la plataforma bilingüe que transforma a tu familia en el plan de estudios de tu hijo. En lugar de \"M de Mesa\", aprende \"M de Mamá\" con la foto real de su mamá.",
        btnFree: "Prueba Gratis 7 Días",
        btnVideo: "Ver cómo funciona (2 min)",
        reviews: "4.6/5 (114 reviews)",
        check1: "Aprende a leer en español mientras construye vocabulario en inglés",
        check2: "Usa fotos y voces de tu familia (no dibujos genéricos)",
        check3: "15 minutos al día, resultados medibles en semanas",
        check4: "Evaluación con IA (Taxonomía de Bloom) integrada",
      },
      problem: {
        title: "¿Te suena ",
        highlight: "familiar",
        question: "?",
        subtitle: "El problema no es tu hijo: es el método.",
        card1Title: "Batallas con la Tarea",
        card1Text: "\"Lee esta palabra\"... \"No, estoy cansado\". Termina en llanto y frustración todas las noches.",
        card2Title: "Apps Aburridas",
        card2Text: "Prometen magia, pero tu hijo juega 3 días y nunca la vuelve a abrir. Dinero a la basura.",
        card3Text: "En la escuela y con amigos todo es inglés. Sientes que pierde su identidad y conexión contigo.",
        bottomText: "¿Qué tal si aprendiera \"P de Papá\" con la foto REAL de su papá?"
      },
      intro: {
        subtitle: "Conoce miABC+",
        title: "Tu familia ",
        highlight: "ES",
        end: " el contenido educativo",
        desc: "No es otra app. Es una herramienta que convierte el amor familiar en metodología.",
        cardTitle: "Personalización Real",
        cardDesc: "Tu hijo aprende con la FOTO de mamá y la VOZ de los abuelos. El aprendizaje se vuelve conexión.",
        check1: "Usa la cámara de tu dispositivo",
        check2: "Colabora en familia",
        btnOpen: "Descargar App",
        btnMore: "Ver más"
      },
      features: {
        title1: "Bilingüe de Verdad",
        desc1: "Diseñado para que lean en español (método silábico) y construyan vocabulario en inglés simultáneamente.",
        check1_1: "Built-in Modules and Examples",
        check1_2: "Lectura continua",
        title2: "Evaluación con IA",
        desc2: "Genera preguntas basadas en la Taxonomía de Bloom para desarrollar pensamiento crítico, no solo memoria.",
        check2_1: "Built-in Modules and Exams",
        check2_2: "Pensamiento",
        sectionTitle: "Todo lo que necesitas en ",
        sectionHighlight: "un solo lugar",
        f1Subtitle: "Funcionalidad 1",
        f1Title: "App + Portal Web",
        f1_1: "iOS, Android, Web",
        f1_2: "Sincronización automática",
        f1_3: "Usa en tablet, celular, computadora",
        f1_4: "Sin conexión: Descarga y practica offline",
        f2Subtitle: "Funcionalidad 2",
        f2Title: "184 Tarjetas Físicas",
        f2_1: "Lavables y resistentes",
        f2_2: "Aprendizaje híbrido (digital + táctil)",
        f2_3: "Para juegos de memoria",
        f2_4: "Práctica sin pantalla",
        f3Subtitle: "Funcionalidad 3",
        f3Title: "10 Módulos Pedagógicos",
        f3text: "Alfabeto • Sonidos • Números • Palabras • Completar • Familia • Colores • Figuras • Lectura • Escritura",
        f3_1: "Progresión estructurada, no caótica",
        f4Subtitle: "Funcionalidad 4",
        f4Title: "IA Generativa",
        f4_1: "Crea textos personalizados",
        f4_2: "Genera preguntas de comprensión",
        f4_3: "Evalúa con Taxonomía de Bloom",
        f4_4: "Se adapta al nivel del niño",
        f5Subtitle: "Funcionalidad 5",
        f5Title: "Módulo Familia",
        f5_1: "Agrega abuelos, hermanos, mascotas",
        f5_2: "Graba voces de cada miembro (digital + táctil)",
        f5_3: "Frases personalizadas de felicitación",
        f5_4: "Tu familia = Su vocabulario",
        f6Title: "Reportes en Tiempo Real",
        f6_1: "Dashboard para padres/maestros",
        f6_2: "Progreso por módulo",
        f6_3: "Tiempo de uso",
        f6_4: "Comprensión lectora medida",
        f6_5: "Exporta reportes PDF",
        btnExplore: "Explora Todas las Funciones",
        subtitleExplore: "Sin tarjeta de crédito"
      },
      howItWorks: {
        title: "Cómo funciona miABC+",
        subtitle: "3 Pasos simples. 15 minutos al día.",
        step1: "Paso 1",
        title1: "Semana 1: Personaliza",
        desc1: "Sube fotos de tu familia y graba tu voz (toma 10 min). Agrega los nombres de mascotas y primos.",
        step2: "Paso 2",
        title2: "Semanas 2-12: Practica",
        desc2: "Tu hijo usa la app 15 min al día. Escucha TU voz cuando acierta y ve TUS fotos cuando practica el idioma.",
        step3: "Paso 3",
        title3: "Día 1-90: Monitorea",
        desc3: "Recibe reportes en tiempo real en tu dashboard. Datos objetivos sobre su comprensión lectora.",
        btnTrial: "Prueba Gratis 7 Días",
        subtitleTrial: "Sin tarjeta de crédito"
      },
      testimonials: {
        title: "Más de 10,000 familias felices",
        subtitle: "Todos ellos ya confían en miABC+",
        t1Text: "Es divertido porque ves a tu familia mientras aprendes. Puedes añadir palabras que no estén y añadir a tus mascotas.",
        t1Author: "Rodrigo (11 años)",
        t1Role: "Usuario miABC+",
        t2Text: "Mi hijo llevaba 2 años respondiéndome solo en inglés. Con miABC+, en 8 semanas empezó a PEDIR practicar porque escucha mi voz. No solo recuperé el idioma, recuperé mi conexión con él.",
        t2Author: "Carmen R.",
        t2Role: "Mamá de Santiago (6 años), Los Angeles",
        t3Text: "Me encantan las imágenes y que puedo ver a mi perrito mientras aprendo. Es mi app favorita.",
        t3Author: "Sofía (8 años)",
        t3Role: "Usuario miABC+",
        t4Text: "Increíble. Mi hijo aprendió más en 3 semanas con miABC+ que en 6 meses con otras aplicaciones populares.",
        t4Author: "Marco G.",
        t4Role: "Papá de Leo (5 años)",
        t5Text: "Soy maestra y recomiendo miABC+ a todos mis representantes. Los reportes de progreso son detallados y objetivos.",
        t5Author: "Elena S.",
        t5Role: "Maestra de Primaria"
      },
      pricing: {
        title: "Planes transparentes",
        subtitle: "Acceso completo a todos los módulos y personalización ilimitada.",
        card1Title: "Plan Básico",
        card1Price: "4",
        card1Sub: "Pago Único (Acceso Digital)",
        c1_1: "Acceso total a la App",
        c1_2: "Incluye tarjetas digitales",
        c1_3: "Sin caja física",
        c1_4: "Soporte por email",
        btn1: "Comprar Ahora",
        c1Footer: "Acceso instantáneo",
        card2Title: "Plan Completo + Caja",
        card2Price: "10",
        card2Sub: "Pago Único (Físico + Digital)",
        c2_1: "Todo lo del plan básico",
        c2_2: "Caja MIABC incluida",
        c2_3: "184 Tarjetas físicas",
        c2_4: "Envío a domicilio",
        btn2: "Comprar Ahora",
        c2Footer: "Garantía de satisfacción",
        footerInfo: "• Dashboard para padres • Soporte técnico • Actualizaciones gratuitas"
      },
      faq: {
        title: "FAQ",
        q1: "¿Realmente es gratis la prueba?",
        a1: "Sí. 7 días completos sin tarjeta de crédito. Acceso total.",
        q2: "¿Puedo usar con varios hijos?",
        a2: "Sí, puedes registrar y personalizar el avance para diferentes niños en la misma cuenta.",
        q3: "¿Qué pasa si no funciona para mi hijo?",
        a3: "Ofrecemos garantía de satisfacción para asegurar tu tranquilidad. Contáctanos si necesitas ayuda adicional.",
        q4: "¿Para qué edades es miABC+?",
        a4: "Está diseñado principalmente para niños entre 3 y 8 años, dependiendo del ritmo individual de aprendizaje.",
        q5: "¿Funciona sin internet?",
        a5: "Sí, la aplicación móvil permite descargar contenido para practicar offline cuando no tengas conexión."
      },
      footer: {
        privacy: "Política de Privacidad",
        developed: "Desarrollado por",
        web: "Web",
        email: "Email",
        userGuide: "Guía de usuario"
      },
      contactSection: {
        title: "¿Tienes alguna ",
        highlight: "duda",
        question: "?",
        subtitle: "Estamos aquí para ayudarte a transformar el aprendizaje de tu hijo.",
        name: "Nombre completo",
        email: "Correo electrónico",
        message: "Tu mensaje",
        btnSend: "Enviar Mensaje",
        success: "¡Mensaje enviado con éxito!",
        error: "Hubo un error al enviar el mensaje."
      }
    },
    en: {
      nav: { login: "Login", contact: "Contact Us", banner: "Family Personalization + AI + Proven pedagogical method = Results in 90 days. Learn more.", problem: "Symptoms", features: "Features", howItWorks: "How it works", testimonials: "Testimonials", pricing: "Pricing", faq: "FAQ", freeTrial: "7-Day Free Trial" },
      hero: {
        title: "The only app where your child learns to read",
        highlight: " seeing the people they love.",
        tabParents: "For Parents",
        tabSchools: "For Schools",
        tabOrgs: "NGOs & Govt",
        cardTitle: "is the bilingual platform that transforms your family into your child's curriculum. Instead of 'A for Apple', they learn 'D for Dad' with real family photos.",
        btnFree: "Start 7-Day Free Trial",
        btnVideo: "Watch how it works (2 min)",
        reviews: "4.6/5 (114 reviews)",
        check1: "Learn to read in Spanish while building English vocabulary",
        check2: "Uses photos and voices of your family (no generic drawings)",
        check3: "15 minutes a day, measurable results in weeks",
        check4: "Integrated AI assessment (Bloom's Taxonomy)"
      },
      problem: {
        title: "Does this sound ",
        highlight: "familiar",
        question: "?",
        subtitle: "The problem isn't your child: it's the method.",
        card1Title: "Homework Battles",
        card1Text: "\"Read this word\"... \"No, I'm tired\". Ends in tears and frustration every night.",
        card2Title: "Boring Apps",
        card2Text: "They promise magic, but your child plays 3 days and never opens it again. Money down the drain.",
        card3Text: "At school and with friends, everything is English. You feel they're losing their identity and connection with you.",
        bottomText: "What if they learned 'D for Dad' with their REAL dad's photo?"
      },
      intro: {
        subtitle: "Meet miABC+",
        title: "Your family ",
        highlight: "IS",
        end: " the educational content",
        desc: "It's not just another app. It's a tool that turns family love into methodology.",
        cardTitle: "Real Personalization",
        cardDesc: "Your child learns with Mom's PHOTO and Grandparent's VOICE. Learning becomes connection.",
        check1: "Use your device's camera",
        check2: "Collaborate as a family",
        btnOpen: "Download App",
        btnMore: "See more"
      },
      features: {
        title1: "Truly Bilingual",
        desc1: "Designed to read in Spanish (syllabic method) and build vocabulary in English simultaneously.",
        check1_1: "Built-in Modules and Examples",
        check1_2: "Continuous reading",
        title2: "AI Assessment",
        desc2: "Generates questions based on Bloom's Taxonomy to develop critical thinking, not just memory.",
        check2_1: "Built-in Modules and Exams",
        check2_2: "Critical thinking",
        sectionTitle: "Everything you need in ",
        sectionHighlight: "one place",
        f1Subtitle: "Feature 1",
        f1Title: "App + Web Portal",
        f1_1: "iOS, Android, Web",
        f1_2: "Automatic synchronization",
        f1_3: "Use on tablet, phone, computer",
        f1_4: "Offline: Download and practice",
        f2Subtitle: "Feature 2",
        f2Title: "184 Physical Flashcards",
        f2_1: "Washable and durable",
        f2_2: "Hybrid learning (digital + tactile)",
        f2_3: "For memory games",
        f2_4: "Screen-free practice",
        f3Subtitle: "Feature 3",
        f3Title: "10 Pedagogical Modules",
        f3text: "Alphabet • Sounds • Numbers • Words • Complete • Family • Colors • Shapes • Reading • Writing",
        f3_1: "Structured progression, not chaotic",
        f4Subtitle: "Feature 4",
        f4Title: "Generative AI",
        f4_1: "Creates personalized texts",
        f4_2: "Generates comprehension questions",
        f4_3: "Evaluates with Bloom's Taxonomy",
        f4_4: "Adapts to the child's level",
        f5Subtitle: "Feature 5",
        f5Title: "Family Module",
        f5_1: "Add grandparents, siblings, pets",
        f5_2: "Record voices of each member",
        f5_3: "Personalized congratulation phrases",
        f5_4: "Your family = Their vocabulary",
        f6Title: "Real-Time Reports",
        f6_1: "Dashboard for parents/teachers",
        f6_2: "Progress per module",
        f6_3: "Usage time",
        f6_4: "Measured reading comprehension",
        f6_5: "Export PDF reports",
        btnExplore: "Explore All Features",
        subtitleExplore: "No credit card required"
      },
      howItWorks: {
        title: "How miABC+ works",
        subtitle: "3 Simple steps. 15 minutes a day.",
        step1: "Step 1",
        title1: "Week 1: Personalize",
        desc1: "Upload family photos and record your voice (takes 10 min). Add names of pets and cousins.",
        step2: "Step 2",
        title2: "Weeks 2-12: Practice",
        desc2: "Your child uses the app 15 mins a day. Hears YOUR voice when correct and sees YOUR photos while practicing.",
        step3: "Step 3",
        title3: "Day 1-90: Monitor",
        desc3: "Receive real-time reports on your dashboard. Objective data on their reading comprehension.",
        btnTrial: "7-Day Free Trial",
        subtitleTrial: "No credit card required"
      },
      testimonials: {
        title: "Over 10,000 happy families",
        subtitle: "They all trust miABC+",
        t1Text: "It's fun because you see your family while you learn. You can add words that aren't there and add your pets.",
        t1Author: "Rodrigo (11 years old)",
        t1Role: "miABC+ User",
        t2Text: "My son spent 2 years responding to me only in English. With miABC+, in 8 weeks he started ASKING to practice because he hears my voice. I didn't just recover the language, I recovered my connection with him.",
        t2Author: "Carmen R.",
        t2Role: "Mom of Santiago (6 years old), Los Angeles",
        t3Text: "I love the pictures and that I can see my puppy while I learn. It's my favorite app.",
        t3Author: "Sofia (8 years old)",
        t3Role: "miABC+ User",
        t4Text: "Incredible. My son learned more in 3 weeks with miABC+ than in 6 months with other popular apps.",
        t4Author: "Marco G.",
        t4Role: "Leo's Dad (5 years old)",
        t5Text: "I'm a teacher and I recommend miABC+ to all my parents. The progress reports are detailed and objective.",
        t5Author: "Elena S.",
        t5Role: "Elementary Teacher"
      },
      pricing: {
        title: "Transparent Plans",
        subtitle: "Full access to all modules and unlimited personalization.",
        card1Title: "Basic Plan",
        card1Price: "4",
        card1Sub: "One-time Payment (Digital Access)",
        c1_1: "Full App access",
        c1_2: "Includes digital flashcards",
        c1_3: "No physical box",
        c1_4: "Email support",
        btn1: "Buy Now",
        c1Footer: "Instant access",
        card2Title: "Full Plan + Box",
        card2Price: "10",
        card2Sub: "One-time Payment (Physical + Digital)",
        c2_1: "Everything in basic plan",
        c2_2: "MIABC Box included",
        c2_3: "184 Physical flashcards",
        c2_4: "Home delivery",
        btn2: "Buy Now",
        c2Footer: "Satisfaction guarantee",
        footerInfo: "• Parent Dashboard • Technical Support • Free Updates"
      },
      faq: {
        title: "FAQ",
        q1: "Is the trial really free?",
        a1: "Yes. 7 full days with no credit card required. Full access.",
        q2: "Can I use it with multiple children?",
        a2: "Yes, you can register and track progress for different children within the same account.",
        q3: "What if it doesn't work for my child?",
        a3: "We offer a satisfaction guarantee to ensure your peace of mind. Contact us if you need further help.",
        q4: "What ages is miABC+ designed for?",
        a4: "It is designed primarily for children between 3 and 8 years old, depending on the individual pace of learning.",
        q5: "Does it work without internet?",
        a5: "Yes, the mobile app allows you to download content for offline practice when you don't have a connection."
      },
      footer: {
        privacy: "Privacy Policy",
        developed: "Developed by",
        web: "Web",
        email: "Email",
        userGuide: "User Guide"
      },
      contactSection: {
        title: "Have any ",
        highlight: "questions",
        question: "?",
        subtitle: "We're here to help you transform your child's learning.",
        name: "Full Name",
        email: "Email Address",
        message: "Your Message",
        btnSend: "Send Message",
        success: "Message sent successfully!",
        error: "There was an error sending the message."
      }
    },
    ta: {
      nav: { login: "உள்நுழைய", contact: "தொடர்பு கொள்க", banner: "குடும்பத் தனிப்பயனாக்கம் முதலியன 90 நாட்களில் முடிவுகள். மேலும் அறிய.", problem: "அறிகுறிகள்", features: "அம்சங்கள்", howItWorks: "எப்படிச் செயல்படுகிறது", testimonials: "சான்றுதழ்கள்", pricing: "விலை", faq: "FAQ", freeTrial: "இலவச சோதனை" },
      hero: {
        title: "உங்கள் குழந்தை পড়তেக் கற்றுக் கொள்ளும் ஒரே பயன்பாடு",
        highlight: " அவர்கள் விரும்பும் நபர்களைப் பார்ப்பதன் மூலம்.",
        tabParents: "பெற்றோர்களுக்கு",
        tabSchools: "பள்ளிகளுக்கு",
        tabOrgs: "அரசு சாரா நிறுவனங்கள்",
        cardTitle: "என்பது இருமொழி தளமாகும்...",
        btnFree: "இலவச சோதனை ஆரம்பிக்க",
        btnVideo: "எப்படிச் செயல்படுகிறது (2 நிமிடம்)",
        reviews: "4.6/5 (114 மதிப்பாய்வுகள்)",
        check1: "ஆங்கிலச் சொற்களஞ்சியத்தை வளர்க்கும் போது ஸ்பானிஷ் மொழியில் படிக்கக் கற்றுக்கொள்ளுங்கள்",
        check2: "உங்கள் குடும்பத்தின் புகைப்படங்களையும் குரல்களையும் பயன்படுத்துகிறது",
        check3: "ஒரு நாளைக்கு 15 நிமிடங்கள், அளவிடக்கூடிய முடிவுகள்",
        check4: "ஒருங்கிணைந்த AI மதிப்பீடு"
      },
      problem: {
        title: "இது ",
        highlight: "பழக்கமானதாக",
        question: " தெரிகிறதா?",
        subtitle: "பிரச்சனை உங்கள் குழந்தை அல்ல: இது முறை.",
        card1Title: "வீட்டுப்பாடம் போர்கள்",
        card1Text: "அழுகையுடனும் ஏமாற்றத்துடனும் ஒவ்வொரு இரவும் முடிகிறது.",
        card2Title: "சலிப்பான பயன்பாடுகள்",
        card2Text: "அவர்கள் மாயாஜாலம் செய்வதாக உறுதியளிக்கிறார்கள், ஆனால் உங்கள் குழந்தை 3 நாட்கள் விளையாடிவிட்டு அதை மீண்டும் திறக்க மாட்டார்கள்.",
        card3Text: "பள்ளியிலும் நண்பர்களிடமும், எல்லாம் ஆங்கிலம்.",
        bottomText: "அவர்கள் உண்மையான அப்பாவின் புகைப்படத்துடன் கற்றுக்கொண்டால் என்ன செய்வது?"
      },
      intro: {
        subtitle: "miABC+ ஐ சந்திக்கவும்",
        title: "உங்கள் குடும்பம் ",
        highlight: "தான்",
        end: " கல்வி உள்ளடக்கம்",
        desc: "இது மற்றொரு பயன்பாடு மட்டுமல்ல.",
        cardTitle: "உண்மையான தனிப்பயனாக்கம்",
        cardDesc: "உங்கள் குழந்தை அம்மாவின் புகைப்படம் மற்றும் தாத்தாவின் குரல் மூலம் கற்றுக்கொள்கிறது.",
        check1: "உங்கள் சாதனத்தின் கேமராவைப் பயன்படுத்தவும்",
        check2: "ஒரு குடும்பமாக ஒத்துழைக்கவும்",
        btnOpen: "பயன்பாட்டைப் பதிவிறக்கவும்",
        btnMore: "மேலும் பார்க்க"
      },
      features: {
        title1: "உண்மையான இருமொழி",
        desc1: "ஸ்பானிஷ் படிக்கவும் மேலும் ஆங்கிலத்தில் சொல்லகராதி உருவாக்கவும் வடிவமைக்கப்பட்டுள்ளது.",
        check1_1: "உள்ளமைக்கப்பட்ட தொகுதிகள்",
        check1_2: "தொடர்ச்சியான வாசிப்பு",
        title2: "AI மதிப்பீடு",
        desc2: "ப்ளூமின் வகைபிரிப்பை அடிப்படையாகக் கொண்ட கேள்விகளை உருவாக்குகிறது.",
        check2_1: "தேர்வுகள்",
        check2_2: "விமர்சன சிந்தனை",
        sectionTitle: "உங்களுக்குத் தேவையான அனைத்தும் ",
        sectionHighlight: "ஒரே இடத்தில்",
        f1Subtitle: "அம்சம் 1",
        f1Title: "ஆப் + இணையதளம்",
        f1_1: "iOS, Android, Web",
        f1_2: "தானியங்கி ஒத்திசைவு",
        f1_3: "கணினியில் பயன்படுத்தவும்",
        f1_4: "ஆஃப்லைன் பயிற்சி",
        f2Subtitle: "அம்சம் 2",
        f2Title: "184 கார்டுகள்",
        f2_1: "கழுவக்கூடியவை",
        f2_2: "கலப்பின கற்றல்",
        f2_3: "நினைவக விளையாட்டுகள்",
        f2_4: "திரை இல்லாத பயிற்சி",
        f3Subtitle: "அம்சம் 3",
        f3Title: "10 தொகுதிகள்",
        f3text: "எழுத்துக்கள்",
        f3_1: "கட்டமைக்கப்பட்ட முன்னேற்றம்",
        f4Subtitle: "அம்சம் 4",
        f4Title: "AI உருவாக்கம்",
        f4_1: "தனிப்பயனாக்கப்பட்ட உரைகள்",
        f4_2: "கேள்விகளை உருவாக்குதல்",
        f4_3: "மதிப்பீடு செய்தல்",
        f4_4: "குழந்தைக்கு ஏற்றவாறு",
        f5Subtitle: "அம்சம் 5",
        f5Title: "குடும்ப தொகுதி",
        f5_1: "தாத்தா, பாட்டி சேர்",
        f5_2: "குரல்களை பதிவு செய்",
        f5_3: "தனிப்பட்ட வம்சங்கள்",
        f5_4: "குடும்பம் = சொல்லகராதி",
        f6Title: "நிகழ்நேர அறிக்கைகள்",
        f6_1: "பெற்றோர் டாஷ்போர்டு",
        f6_2: "முன்னேற்றம்",
        f6_3: "பயன்பாட்டு நேரம்",
        f6_4: "வாசிப்பு புரிதல்",
        f6_5: "PDF அறிக்கைகள்",
        btnExplore: "அனைத்தையும் ஆராயுங்கள்",
        subtitleExplore: "கிரெடிட் கார்டு தேவையில்லை"
      },
      howItWorks: {
        title: "miABC+ எவ்வாறு செயல்படுகிறது",
        subtitle: "3 எளிய படிகள். தினமும் 15 நிமிடம்.",
        step1: "படி 1",
        title1: "வாரம் 1: தனிப்பயனாக்கு",
        desc1: "புகைப்படங்களை பதிவேற்றவும் மற்றும் குரலைப் பதிவு செய்யவும்.",
        step2: "படி 2",
        title2: "வாரங்கள் 2-12: பயிற்சி",
        desc2: "உங்கள் குழந்தை தினமும் 15 நிமிடம் பயன்பாட்டைப் பயன்படுத்துகிறது. சரியாகச் சொல்லும்போது உங்கள் குரலைக் கேட்கிறார்கள் மற்றும் உங்கள் புகைப்படங்களைப் பார்க்கிறார்கள்.",
        step3: "படி 3",
        title3: "நாள் 1-90: கண்காணிக்கவும்",
        desc3: "உங்கள் டாஷ்போர்டில் நிகழ்நேர அறிக்கைகளைப் பெறுங்கள். அவர்களின் வாசிப்புப் புரிதல் குறித்த தரவு.",
        btnTrial: "இலவச சோதனை ஆரம்பிக்க",
        subtitleTrial: "கடன் அட்டை தேவையில்லை"
      },
      testimonials: {
        title: "10,000 க்கும் மேற்பட்டவர்கள்",
        subtitle: "அவர்கள் miABC+ ஐ நம்புகிறார்கள்",
        t1Text: "இது வேடிக்கையானது, நீங்கள் கற்கும் போது உங்கள் குடும்பத்தைப் பார்க்கலாம்.",
        t1Author: "ரோட்ரிகோ (11 வயது)",
        t1Role: "பயனர்",
        t2Text: "ஆங்கிலத்தில் மட்டுமே பதிலளித்தார். இப்போது அவர் பயிற்சி கேட்கிறார்.",
        t2Author: "கார்மென்",
        t2Role: "சாண்டியாகோவின் அம்மா",
        t3Text: "நான் படங்களை விரும்புகிறேன் மற்றும் கற்கும் போது எனது நாய்க்குட்டியைக் காணலாம்.",
        t3Author: "சோபியா (8 வயது)",
        t3Role: "பயனர்",
        t4Text: "அற்புதமானது. மற்ற அப்-களை விட 3 வாரங்களில் எனது மகன் அதிகமாக கற்றுக்கொண்டார்.",
        t4Author: "மார்கோ ஜி.",
        t4Role: "லியோவின் தந்தை",
        t5Text: "நான் ஒரு ஆசிரியர், miABC+ ஐ அனைவருக்கும் பரிந்துரைக்கிறேன். அறிக்கைகள் மிகவும் பயனுள்ளதாக உள்ளன.",
        t5Author: "எலெனா எஸ்.",
        t5Role: "ஆசிரியர்"
      },
      pricing: {
        title: "வெளிப்படையான திட்டங்கள்",
        subtitle: "அனைத்து தொகுதிகளுக்கும் முழு அணுகல் மற்றும் வரம்பற்ற தனிப்பயனாக்கம்.",
        card1Title: "அடிப்படை திட்டம்",
        card1Price: "4",
        card1Sub: "ஒருமுறை கட்டணம் (டிஜிட்டல் அணுகல்)",
        c1_1: "முழு அணுகல்",
        c1_2: "டிஜிட்டல் கார்டுகள்",
        c1_3: "பெட்டி இல்லை",
        c1_4: "மின்னஞ்சல் ஆதரவு",
        btn1: "இப்போது வாங்கு",
        c1Footer: "உடனடி அணுகல்",
        card2Title: "முழுமையான திட்டம் + பெட்டி",
        card2Price: "10",
        card2Sub: "ஒருமுறை கட்டணம் (பெட்டி + டிஜிட்டல்)",
        c2_1: "அடிப்படை திட்டத்தில் அனைத்தும்",
        c2_2: "பெட்டி சேர்க்கப்பட்டுள்ளது",
        c2_3: "184 கார்டுகள்",
        c2_4: "வீட்டு விநியோகம்",
        btn2: "இப்போது வாங்கு",
        c2Footer: "திருப்தி உத்தரவாதம்",
        footerInfo: "• பெற்றோர் டாஷ்போர்டு • ஆதரவு • இலவச புதுப்பிப்புகள்"
      },
      faq: {
        title: "FAQ",
        q1: "சோதனை உண்மையில் இலவசமா?",
        a1: "ஆம். 7 முழு நாட்கள் இலவசம்.",
        q2: "பல குழந்தைகளுடன் இதைப் பயன்படுத்தலாமா?",
        a2: "ஆம், பல குழந்தைகளை பதிவு செய்யலாம்.",
        q3: "என் குழந்தைக்கு வேலை செய்யவில்லை என்றால் என்ன?",
        a3: "நாங்கள் திருப்தி உத்தரவாதம் அளிக்கிறோம்.",
        q4: "miABC+ எந்த வயதினருக்கும் வடிவமைக்கப்பட்டுள்ளது?",
        a4: "இது 3 முதல் 8 வயது வரையுள்ள குழந்தைகளுக்காக வடிவமைக்கப்பட்டுள்ளது.",
        q5: "இணையம் இல்லாமல் செயல்படுமா?",
        a5: "ஆம், ஆஃப்லைனில் படிக்கலாம்."
      },
      footer: {
        privacy: "தனியுரிமைக் கொள்கை",
        developed: "உருவாக்கியவர்",
        web: "இணையதளம்",
        email: "மின்னஞ்சல்",
        userGuide: "பயனர் வழிகாட்டி"
      },
      contactSection: {
        title: "உங்களுக்கு ஏதேனும் ",
        highlight: "கேள்விகள்",
        question: " உள்ளனவா?",
        subtitle: "உங்கள் குழந்தையின் கற்றலை மாற்ற நாங்கள் உங்களுக்கு உதவ இங்கே இருக்கிறோம்.",
        name: "முழு பெயர்",
        email: "மின்னஞ்சல் முகவரி",
        message: "உங்கள் செய்தி",
        btnSend: "செய்தி அனுப்பவும்",
        success: "செய்தி வெற்றிகரமாக அனுப்பப்பட்டது!",
        error: "செய்தி அனுப்புவதில் பிழை ஏற்பட்டது."
      }
    }
  }

  const t = translations[language] || translations.es

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index)
  }

  return (
    <div className="landing-body">
      {/* Top Thin Banner */}
      <div className="top-thin-banner">
        <div className="banner-text">
          {t.nav.banner}
        </div>
        <div className="banner-links">
          <Link to="/login" style={{ color: "white", textDecoration: "none" }}>{t.nav.login}</Link>
          <a href="#contact" style={{ color: "white", textDecoration: "none" }}>{t.nav.contact}</a>
        </div>
      </div>

      {/* Main Header Menu */}
      <div className="main-header-menu">
        <div className="header-logo-container">
          <img src="/assets/img/landing-images/MiABC-01.png" alt="MiABC Logo" style={{ height: "50px" }} />
        </div>
        
        <div className="main-header-links">
          <a href="#problem" style={{ color: "white", textDecoration: "none", fontSize: "0.95rem" }}>{t.nav.problem}</a>
          <a href="#how-it-works" style={{ color: "white", textDecoration: "none", fontSize: "0.95rem" }}>{t.nav.howItWorks}</a>
          <a href="#testimonials" style={{ color: "white", textDecoration: "none", fontSize: "0.95rem" }}>{t.nav.testimonials}</a>
          <a href="#schools" style={{ color: "white", textDecoration: "none", fontSize: "0.95rem" }}>{language === "en" ? "Schools" : language === "ta" ? "பள்ளிகள்" : "Escuelas"}</a>
          <a href="#faq" style={{ color: "white", textDecoration: "none", fontSize: "0.95rem" }}>{t.nav.faq}</a>
        </div>

        <div className="main-header-actions">
          <a href="#contact" className="btn-secondary-new" style={{ padding: "10px 25px" }}>{t.nav.contact}</a>
          <div style={{ zIndex: 1000, marginLeft: "10px" }}>
            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* HEADER / HERO SECTION */}
      <div className="section-container">
        <div className="hero-grid">
          <div>
            <h1 className="h1-hero">
              {t.hero.title}
              <span className="highlight-orange">{t.hero.highlight}</span>
            </h1>
            
            <div className="hero-tabs">
              <div 
                className={`hero-tab ${activeTab === 'padres' ? 'active' : ''}`}
                onClick={() => setActiveTab('padres')}
              >
                {t.hero.tabParents}
              </div>
              <div 
                className={`hero-tab ${activeTab === 'escuelas' ? 'active' : ''}`}
                onClick={() => setActiveTab('escuelas')}
              >
                {t.hero.tabSchools}
              </div>
              <div 
                className={`hero-tab ${activeTab === 'ongs' ? 'active' : ''}`}
                onClick={() => setActiveTab('ongs')}
              >
                {t.hero.tabOrgs}
              </div>
            </div>

            <div className="hero-card">
              <img src="/assets/img/landing-images/MiABC-horizontal.png" alt="MiABC Logo" style={{ height: "30px", marginBottom: "15px" }} />
              <p className="hero-card-title">{t.hero.cardTitle}</p>
              
              <div style={{ display: "flex", gap: "15px", flexWrap: "wrap", alignItems: "center" }}>
                <a href="#contact" className="btn-primary-new">{t.nav.contact}</a>
                <a href="#" style={{ color: "white", textDecoration: "none", display: "flex", alignItems: "center", gap: "5px" }}>
                  {t.hero.btnVideo} →
                </a>
              </div>
            </div>

          </div>

          <div>
            <div className="hero-image-wrapper">
              {/* Optional Placeholder for Hero Image */}
              <div style={{width: "100%", height: "350px", borderRadius: "20px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden"}}>
                 <img src="/assets/img/landing-images/image1.png" alt="MiABC App" style={{objectFit: "cover", width: "100%", height: "100%"}} />
              </div>
            </div>

            <div className="hero-rating">
              <span style={{backgroundColor: "#ff4f2a", color: "white", padding: "5px", borderRadius: "50%", width: "30px", height: "30px", display: "flex", alignItems:"center", justifyContent:"center"}}>G</span>
              <strong>{t.hero.reviews}</strong>
            </div>

            <ul className="hero-checks">
              <li><span>✓</span> {t.hero.check1}</li>
              <li><span>✓</span> {t.hero.check2}</li>
              <li><span>✓</span> {t.hero.check3}</li>
              <li><span>✓</span> {t.hero.check4}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* LOGOS STRIP */}
      <div className="logos-strip">
        <div className="logos-container">
           <span className="brand-item">COMPASS</span>
           <span className="brand-item">HARVARD UNIVERSITY</span>
           <span className="brand-item">Johnson&Johnson</span>
           <span className="brand-item">Ray-Ban</span>
           <span className="brand-item">SIEMENS</span>
           <span className="brand-item">twilio</span>
           <span className="brand-item">toptal</span>
           <span className="brand-item">vodafone</span>
        </div>
      </div>

      {/* PROBLEM SECTION */}
      <div id="problem" className="section-container bg-white-section section-full text-center" style={{paddingTop: "80px", paddingBottom: "80px"}}>
        <div style={{maxWidth: "1000px", margin: "0 auto"}}>
          <h2 style={{fontSize: "2.5rem", fontWeight: "bold", color: "#333"}}>{t.problem.title}<span className="highlight-pink">{t.problem.highlight}</span>{t.problem.question}</h2>
          <p style={{fontSize: "1.2rem", color: "#666", marginTop: "10px"}}>{t.problem.subtitle}</p>

          <div className="problem-card-grid">
            <div className="problem-card">
              <h3>{t.problem.card1Title}</h3>
              <p>{t.problem.card1Text}</p>
            </div>
            <div className="problem-card">
              <h3>{t.problem.card2Title}</h3>
              <p>{t.problem.card2Text}</p>
            </div>
            <div className="problem-card">
              <p>{t.problem.card3Text}</p>
            </div>
          </div>

          <h3 style={{fontSize: "1.5rem", marginTop: "50px", color: "#333", fontWeight: "bold"}}>{t.problem.bottomText}</h3>
        </div>
      </div>

      {/* CONOCE MIABC+ (Intro features) */}
      <div id="features" className="section-container bg-white-section section-full text-center" style={{backgroundColor: "#f5f5f5", paddingBottom: "80px"}}>
        <div style={{maxWidth: "1000px", margin: "0 auto"}}>
          <p style={{color: "#666", fontSize: "1.1rem"}}>{t.intro.subtitle}</p>
          <h2 style={{fontSize: "2.5rem", fontWeight: "bold", color: "#333", marginTop: "10px"}}>
            {t.intro.title}<span className="highlight-pink">{t.intro.highlight}</span>{t.intro.end}
          </h2>
          <p style={{fontSize: "1.2rem", color: "#666", marginTop: "15px", maxWidth: "600px", margin: "15px auto 40px"}}>
            {t.intro.desc}
          </p>

          <div style={{ backgroundColor: "#ffcbe1", borderRadius: "30px", padding: "50px", display: "flex", flexWrap: "wrap", alignItems: "center", textAlign: "left", gap: "40px" }}>
            <div style={{flex: 1, minWidth: "300px"}}>
               <img src="/assets/img/landing-images/MiABC-horizontal.png" alt="MiABC" style={{ height: "30px", marginBottom: "20px" }} />
               <h3 style={{fontSize: "2rem", color: "#333", marginBottom: "15px"}}>{t.intro.cardTitle}</h3>
               <p style={{color: "#555", fontSize: "1.1rem", marginBottom: "30px"}}>{t.intro.cardDesc}</p>
               
               <div style={{display: "flex", gap: "20px", marginBottom: "30px", flexWrap: "wrap", color: "#555"}}>
                 <span>✓ {t.intro.check1}</span>
                 <span>✓ {t.intro.check2}</span>
               </div>

               <div style={{display: "flex", gap: "15px", alignItems: "center"}}>
                 <a href="https://play.google.com/store/apps/details?id=com.diazapps.miabc&hl=es_419" target="_blank" rel="noopener noreferrer" className="btn-primary-new">{t.intro.btnOpen}</a>
                 <a href="#" style={{color: "#eb2178", border: "1px solid #eb2178", padding: "10px 20px", borderRadius: "20px", textDecoration: "none"}}>{t.intro.btnMore} →</a>
               </div>
            </div>
            <div style={{flex: 1, minWidth: "300px"}}>
               <img src="/assets/img/landing-images/image4.png" alt="Real Personalization" style={{width: "100%", borderRadius: "20px"}} />
            </div>
          </div>

          <div className="features-container">
            <div className="feature-block feature-bg-blue" style={{textAlign: "left"}}>
               <img src="/assets/img/landing-images/MiABC-horizontal.png" alt="MiABC Logo" style={{ height: "30px", marginBottom: "20px" }} />
              <h3>{t.features.title1}</h3>
              <p style={{color: "#555"}}>{t.features.desc1}</p>
              <ul className="feature-checks" style={{color: "#555"}}>
                <li>✓ {t.features.check1_1}</li>
                <li>✓ {t.features.check1_2}</li>
              </ul>
              <div style={{display: "flex", gap: "10px", marginTop: "20px"}}>
                 <a href="https://play.google.com/store/apps/details?id=com.diazapps.miabc&hl=es_419" target="_blank" rel="noopener noreferrer" className="feature-btn btn-solid-dark">{t.intro.btnOpen}</a>
                 <a href="#" className="feature-btn btn-outline-dark">{t.intro.btnMore} →</a>
              </div>
               <img src="/assets/img/landing-images/mockup-1.png" alt="Bilingue de verdad" className="feature-image-mockup" />
            </div>

            <div className="feature-block feature-bg-green" style={{textAlign: "left"}}>
              <img src="/assets/img/landing-images/MiABC-horizontal.png" alt="MiABC Logo" style={{ height: "30px", marginBottom: "20px" }} />
              <h3>{t.features.title2}</h3>
              <p style={{color: "#555"}}>{t.features.desc2}</p>
              <ul className="feature-checks" style={{color: "#555"}}>
                <li>✓ {t.features.check2_1}</li>
                <li>✓ {t.features.check2_2}</li>
              </ul>
              <div style={{display: "flex", gap: "10px", marginTop: "20px"}}>
                 <a href="https://play.google.com/store/apps/details?id=com.diazapps.miabc&hl=es_419" target="_blank" rel="noopener noreferrer" className="feature-btn btn-solid-green">{t.intro.btnOpen}</a>
                 <a href="#" className="feature-btn btn-outline-dark">{t.intro.btnMore} →</a>
              </div>
               <img src="/assets/img/landing-images/mockup-2.png" alt="Evaluación con IA" className="feature-image-mockup" />
            </div>
          </div>

        </div>
      </div>

      {/* HOW IT WORKS */}
      <div id="how-it-works" className="section-container bg-plum section-full">
        <div style={{maxWidth: "1000px", margin: "0 auto", textAlign: "center"}}>
          <h2 style={{fontSize: "2.5rem", fontWeight: "bold", marginBottom: "10px"}}>{t.howItWorks.title}</h2>
          <p style={{fontSize: "1.2rem", color: "rgba(255,255,255,0.8)", marginBottom: "60px"}}>{t.howItWorks.subtitle}</p>

          <div className="steps-grid" style={{textAlign: "left"}}>
            <div className="step-item">
              <img src="/assets/img/landing-images/children-ipad.jpg" alt="Step 1" style={{width: "100%", borderRadius: "20px", marginBottom: "0"}} />
            </div>
            <div className="step-item">
              <span className="step-badge">{t.howItWorks.step1}</span>
              <h3>{t.howItWorks.title1}</h3>
              <p style={{fontSize: "1.1rem", color: "rgba(255,255,255,0.8)", lineHeight: "1.6"}}>{t.howItWorks.desc1}</p>
            </div>
            
            <div className="step-item">
              <span className="step-badge step-badge-green">{t.howItWorks.step2}</span>
              <h3>{t.howItWorks.title2}</h3>
              <p style={{fontSize: "1.1rem", color: "rgba(255,255,255,0.8)", lineHeight: "1.6"}}>{t.howItWorks.desc2}</p>
            </div>
            <div className="step-item">
              <img src="/assets/img/landing-images/image3.avif" alt="Step 2" style={{width: "100%", borderRadius: "20px", marginBottom: "0"}} />
            </div>

            <div className="step-item">
              <img src="/assets/img/landing-images/image2.png" alt="Step 3" style={{width: "100%", borderRadius: "20px", marginBottom: "0"}} />
            </div>
            <div className="step-item">
              <span className="step-badge" style={{backgroundColor: "var(--orange-bright)"}}>{t.howItWorks.step3}</span>
              <h3>{t.howItWorks.title3}</h3>
              <p style={{fontSize: "1.1rem", color: "rgba(255,255,255,0.8)", lineHeight: "1.6"}}>{t.howItWorks.desc3}</p>
            </div>
          </div>

          {/* <div style={{marginTop: "50px", display: "flex", flexDirection: "column", alignItems: "center"}}>
             <a href="#" className="btn-outline-white" style={{padding: "15px 40px", fontSize: "1.2rem", borderRadius: "30px", border: "1px solid white", color: "white", textDecoration: "none", width: "fit-content", display: "inline-block"}}>{t.howItWorks.btnTrial}</a>
             <p style={{color: "rgba(255,255,255,0.7)", fontSize: "0.9rem", marginTop: "15px"}}></p>
          </div> */}
        </div>
      </div>

      {/* ADDITIONAL FEATURES GRID / Todo lo que necesitas en un solo lugar */}
      <div className="section-container bg-white-section section-full text-center" style={{paddingTop: "80px", paddingBottom: "80px"}}>
         <h2 style={{fontSize: "2.5rem", fontWeight: "bold", color: "#333", marginBottom: "40px"}}>
           {t.features.sectionTitle}<span className="highlight-pink">{t.features.sectionHighlight}</span>
         </h2>

         <div className="features-container" style={{maxWidth: "1200px", margin: "0 auto"}}>
            {/* Feat 1 */}
            <div className="feature-block feature-bg-green" style={{textAlign: "left"}}>
               <div style={{fontSize: "0.9rem", color: "#555", marginBottom: "20px"}}>{t.features.f1Subtitle}</div>
               <div style={{fontSize: "3rem", marginBottom: "20px"}}>💻📱</div>
               <h3>{t.features.f1Title}</h3>
               <ul className="feature-checks" style={{color: "#555"}}>
                 <li>✓ {t.features.f1_1}</li>
                 <li>✓ {t.features.f1_2}</li>
                 <li>✓ {t.features.f1_3}</li>
                 <li>✓ {t.features.f1_4}</li>
               </ul>
            </div>
            {/* Feat 2 */}
            <div className="feature-block feature-bg-blue" style={{textAlign: "left"}}>
               <div style={{fontSize: "0.9rem", color: "#555", marginBottom: "20px"}}>{t.features.f2Subtitle}</div>
               <div style={{fontSize: "3rem", marginBottom: "20px"}}>🎴</div>
               <h3>{t.features.f2Title}</h3>
               <ul className="feature-checks" style={{color: "#555"}}>
                 <li>✓ {t.features.f2_1}</li>
                 <li>✓ {t.features.f2_2}</li>
                 <li>✓ {t.features.f2_3}</li>
                 <li>✓ {t.features.f2_4}</li>
               </ul>
            </div>
            {/* Feat 3 */}
            <div className="feature-block feature-bg-pink" style={{textAlign: "left"}}>
               <div style={{fontSize: "0.9rem", color: "#555", marginBottom: "20px"}}>{t.features.f3Subtitle}</div>
               <div style={{fontSize: "3rem", marginBottom: "20px"}}>🧩</div>
               <h3 style={{whiteSpace: "pre-line"}}>{t.features.f3Title}</h3>
               <p style={{color: "#555", fontSize: "0.9rem", marginBottom: "20px"}}>{t.features.f3text}</p>
               <ul className="feature-checks" style={{color: "#555"}}>
                 <li>✓ {t.features.f3_1}</li>
               </ul>
            </div>
            {/* Feat 4 */}
            <div className="feature-block feature-bg-blue" style={{textAlign: "left"}}>
               <div style={{fontSize: "0.9rem", color: "#555", marginBottom: "20px"}}>{t.features.f4Subtitle}</div>
               <div style={{fontSize: "3rem", marginBottom: "20px"}}>🤖</div>
               <h3>{t.features.f4Title}</h3>
               <ul className="feature-checks" style={{color: "#555"}}>
                 <li>✓ {t.features.f4_1}</li>
                 <li>✓ {t.features.f4_2}</li>
                 <li>✓ {t.features.f4_3}</li>
                 <li>✓ {t.features.f4_4}</li>
               </ul>
            </div>
            {/* Feat 5 */}
            <div className="feature-block feature-bg-pink" style={{textAlign: "left"}}>
               <div style={{fontSize: "0.9rem", color: "#555", marginBottom: "20px"}}>{t.features.f5Subtitle}</div>
               <div style={{fontSize: "3rem", marginBottom: "20px"}}>👨‍👩‍👦</div>
               <h3>{t.features.f5Title}</h3>
               <ul className="feature-checks" style={{color: "#555"}}>
                 <li>✓ {t.features.f5_1}</li>
                 <li>✓ {t.features.f5_2}</li>
                 <li>✓ {t.features.f5_3}</li>
                 <li>✓ {t.features.f5_4}</li>
               </ul>
            </div>
            {/* Feat 6 */}
            <div className="feature-block feature-bg-green" style={{textAlign: "left"}}>
               <div style={{fontSize: "0.9rem", color: "#555", marginBottom: "20px"}}>Funcionalidad 6</div>
               <div style={{fontSize: "3rem", marginBottom: "20px"}}>📉</div>
               <h3>{t.features.f6Title}</h3>
               <ul className="feature-checks" style={{color: "#555"}}>
                 <li>✓ {t.features.f6_1}</li>
                 <li>✓ {t.features.f6_2}</li>
                 <li>✓ {t.features.f6_3}</li>
                 <li>✓ {t.features.f6_4}</li>
                 <li>✓ {t.features.f6_5}</li>
               </ul>
            </div>
         </div>

         <div style={{marginTop: "50px"}}>
           <a href="#" className="btn-primary-new" style={{padding: "15px 40px", fontSize: "1.2rem"}}>{t.features.btnExplore}</a>
           <p style={{color: "#888", fontSize: "0.9rem", marginTop: "10px"}}></p>
         </div>
      </div>

      {/* TESTIMONIALS */}
      <div id="testimonials" className="section-container bg-plum section-full" style={{paddingBottom: "80px"}}>
         <div style={{maxWidth: "1000px", margin: "0 auto"}}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", marginBottom: "40px"}}>
              <div>
                <h2 style={{fontSize: "2.5rem", fontWeight: "bold"}}>{t.testimonials.title}</h2>
                <p style={{fontSize: "1.1rem", color: "rgba(255,255,255,0.8)"}}>{t.testimonials.subtitle}</p>
              </div>
              <div style={{display: "flex", gap: "10px", marginTop: "20px"}}>
                 <button 
                  onClick={prevTestimonial}
                  style={{background: "none", border: "1px solid white", borderRadius: "50%", width: "50px", height: "50px", color: "white", cursor: "pointer", fontSize: "1.5rem", transition: "all 0.3s"}}
                  className="btn-testimonial-nav"
                 >‹</button>
                 <button 
                  onClick={nextTestimonial}
                  style={{background: "none", border: "1px solid white", borderRadius: "50%", width: "50px", height: "50px", color: "white", cursor: "pointer", fontSize: "1.5rem", transition: "all 0.3s"}}
                  className="btn-testimonial-nav"
                 >›</button>
              </div>
            </div>

            <div className="testimonal-grid">
               {(() => {
                 const allTestimonials = [
                   { text: t.testimonials.t1Text, author: t.testimonials.t1Author, role: t.testimonials.t1Role, img: "/assets/img/testimonials/rodrigo.png" },
                   { text: t.testimonials.t2Text, author: t.testimonials.t2Author, role: t.testimonials.t2Role, img: "/assets/img/testimonials/carmen.png" },
                   { text: t.testimonials.t3Text, author: t.testimonials.t3Author, role: t.testimonials.t3Role, img: "/assets/img/testimonials/sofia.png" },
                   { text: t.testimonials.t4Text, author: t.testimonials.t4Author, role: t.testimonials.t4Role, img: "/assets/img/testimonials/marco.png" },
                   { text: t.testimonials.t5Text, author: t.testimonials.t5Author, role: t.testimonials.t5Role, img: "/assets/img/testimonials/elena.png" },
                 ];
                 
                 // Logic for carousel display
                 let displayed = allTestimonials.slice(testimonalIndex, testimonalIndex + 3);
                 if (displayed.length < 3) {
                   displayed = displayed.concat(allTestimonials.slice(0, 3 - displayed.length));
                 }

                 return displayed.map((test, i) => (
                   <div className="testimonial-card" key={i}>
                      <div className="testimonial-avatar">
                        <img src={test.img} alt={test.author} />
                      </div>
                      <p className="testimonial-text">{test.text}</p>
                      <p className="testimonial-author">{test.author}</p>
                      <p style={{fontSize: "0.9rem", color: "rgba(255,255,255,0.7)"}}>{test.role}</p>
                   </div>
                 ));
               })()}
            </div>
         </div>
      </div>

      {/* CONTACT SECTION */}
      <div id="contact" className="contact-section section-full bg-white-section" style={{paddingTop: "80px", paddingBottom: "80px"}}>
         <div style={{maxWidth: "800px", margin: "0 auto", padding: "0 20px"}}>
            <div className="text-center">
              <h2 style={{fontSize: "2.5rem", fontWeight: "bold", color: "#333"}}>{t.contactSection.title}<span className="highlight-pink">{t.contactSection.highlight}</span>{t.contactSection.question}</h2>
              <p style={{fontSize: "1.2rem", color: "#666", marginTop: "10px", marginBottom: "40px"}}>{t.contactSection.subtitle}</p>
            </div>

            <form onSubmit={handleContactSubmit} className="contact-form-styled">
              <div className="form-grid-2">
                <div className="form-group-custom">
                  <label>{t.contactSection.name}</label>
                  <input 
                    type="text" 
                    required 
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    placeholder={language === 'en' ? "E.g. John Smith" : language === 'ta' ? "எ.கா. ஜான் ஸ்மித்" : "Ej. Juan Pérez"}
                  />
                </div>
                <div className="form-group-custom">
                  <label>{t.contactSection.email}</label>
                  <input 
                    type="email" 
                    required 
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    placeholder={language === 'es' ? "correo@ejemplo.com" : "name@example.com"}
                  />
                </div>
              </div>
              <div className="form-group-custom">
                <label>{t.contactSection.message}</label>
                <textarea 
                  required 
                  rows="5"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  placeholder={language === 'en' ? "Tell us how we can help you..." : language === 'ta' ? "நாங்கள் உங்களுக்கு எப்படி உதவ முடியும் என்று சொல்லுங்கள்..." : "Cuéntanos cómo podemos ayudarte..."}
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                className="btn-primary-new" 
                disabled={submitting}
                style={{width: "100%", marginTop: "20px"}}
              >
                {submitting ? (language === 'en' ? "Sending..." : language === 'ta' ? "அனுப்புகிறது..." : "Enviando...") : t.contactSection.btnSend}
              </button>

              {submitStatus === 'success' && (
                <div className="alert-success-custom">
                  ✓ {t.contactSection.success}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="alert-error-custom">
                  ✕ {t.contactSection.error}
                </div>
              )}
            </form>
         </div>
      </div>

      {/* CAPTACIÓN B2B — Escuelas / Instituciones */}
      <div id="schools" className="section-container bg-plum section-full" style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div style={{ maxWidth: "820px", margin: "0 auto", padding: "0 20px" }}>
          <div className="text-center" style={{ marginBottom: "30px" }}>
            <h2 style={{ fontSize: "2.3rem", fontWeight: "bold", color: "white" }}>
              {language === "en" ? "Are you a school or institution?" : language === "ta" ? "நீங்கள் ஒரு பள்ளியா அல்லது நிறுவனமா?" : "¿Eres una escuela o institución?"}
            </h2>
            <p style={{ fontSize: "1.1rem", color: "rgba(255,255,255,0.85)", marginTop: "10px" }}>
              {language === "en" ? "Tell us about your institution and we'll design a plan for you." : language === "ta" ? "உங்கள் நிறுவனம் பற்றி எங்களிடம் கூறுங்கள்; உங்களுக்கு ஒரு திட்டத்தை வடிவமைப்போம்." : "Cuéntanos sobre tu institución y diseñamos un plan a tu medida."}
            </p>
          </div>

          <form onSubmit={handleLeadSubmit} className="contact-form-styled" style={{ background: "white", padding: "30px", borderRadius: "20px" }}>
            <div className="form-grid-2">
              <div className="form-group-custom">
                <label>{language === "en" ? "Institution name" : language === "ta" ? "நிறுவனத்தின் பெயர்" : "Nombre de la institución"}</label>
                <input type="text" required value={leadForm.institution} onChange={(e) => setLeadForm({ ...leadForm, institution: e.target.value })} />
              </div>
              <div className="form-group-custom">
                <label>{language === "en" ? "Contact name" : language === "ta" ? "தொடர்பு பெயர்" : "Nombre de contacto"}</label>
                <input type="text" required value={leadForm.contactName} onChange={(e) => setLeadForm({ ...leadForm, contactName: e.target.value })} />
              </div>
              <div className="form-group-custom">
                <label>{language === "en" ? "Email" : language === "ta" ? "மின்னஞ்சல்" : "Correo"}</label>
                <input type="email" required value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} />
              </div>
              <div className="form-group-custom">
                <label>{language === "en" ? "Phone" : language === "ta" ? "தொலைபேசி" : "Teléfono"}</label>
                <input type="tel" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} />
              </div>
              <div className="form-group-custom">
                <label>{language === "en" ? "# of teachers" : language === "ta" ? "ஆசிரியர்களின் எண்ணிக்கை" : "Nº de profesores"}</label>
                <input type="number" min="0" value={leadForm.numTeachers} onChange={(e) => setLeadForm({ ...leadForm, numTeachers: e.target.value })} />
              </div>
              <div className="form-group-custom">
                <label>{language === "en" ? "# of students" : language === "ta" ? "மாணவர்களின் எண்ணிக்கை" : "Nº de alumnos"}</label>
                <input type="number" min="0" value={leadForm.numStudents} onChange={(e) => setLeadForm({ ...leadForm, numStudents: e.target.value })} />
              </div>
            </div>
            <div className="form-group-custom">
              <label>{language === "en" ? "Message (optional)" : language === "ta" ? "செய்தி (விருப்பம்)" : "Mensaje (opcional)"}</label>
              <textarea rows="3" value={leadForm.message} onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}></textarea>
            </div>

            <button type="submit" className="btn-primary-new" disabled={leadSubmitting} style={{ width: "100%", marginTop: "15px" }}>
              {leadSubmitting
                ? (language === "en" ? "Sending..." : language === "ta" ? "அனுப்புகிறது..." : "Enviando...")
                : (language === "en" ? "Request information" : language === "ta" ? "தகவலைக் கோருங்கள்" : "Solicitar información")}
            </button>

            {leadStatus === "success" && (
              <div className="alert-success-custom">
                ✓ {language === "en" ? "Thanks! We'll contact you soon." : language === "ta" ? "நன்றி! விரைவில் தொடர்பு கொள்வோம்." : "¡Gracias! Te contactaremos pronto."}
              </div>
            )}
            {leadStatus === "error" && (
              <div className="alert-error-custom">
                ✕ {language === "en" ? "Something went wrong. Please try again." : language === "ta" ? "ஏதோ தவறு. மீண்டும் முயற்சிக்கவும்." : "Ocurrió un error. Intenta de nuevo."}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* PRICING — oculta hasta activar monetización */}
      {false && (
      <div id="pricing" className="pricing-section section-full text-center">
         <h2 style={{fontSize: "2.5rem", fontWeight: "bold", color: "white"}}>{t.pricing.title}</h2>
         <p style={{fontSize: "1.2rem", color: "rgba(255,255,255,0.9)", marginTop: "10px"}}>{t.pricing.subtitle}</p>

         <div className="pricing-grid">
            <div className="pricing-card" style={{backgroundColor: "#ffdbe6"}}>
               <div className="pricing-title">{t.pricing.card1Title}</div>
               <div className="pricing-price">{t.pricing.card1Price} <span>USD</span></div>
               <div className="pricing-subtext">{t.pricing.card1Sub}</div>
               <ul className="pricing-features">
                 <li>✓ {t.pricing.c1_1}</li>
                 <li>✓ {t.pricing.c1_2}</li>
                 <li>✓ {t.pricing.c1_3}</li>
                 {/* <li>✓ {t.pricing.c1_4}</li> */}
               </ul>
               <button className="btn-primary-new" style={{width: "100%", backgroundColor: "#ccc", color:"#555"}}>{t.pricing.btn1}</button>
               <p style={{fontSize: "0.8rem", color: "#666", marginTop: "15px"}}>{t.pricing.c1Footer}</p>
            </div>

            <div className="pricing-card" style={{transform: "scale(1.05)", boxShadow: "0 20px 40px rgba(0,0,0,0.1)", zIndex: 2}}>
               <div className="pricing-title">{t.pricing.card2Title}</div>
               <div className="pricing-price">{t.pricing.card2Price} <span>USD</span></div>
               <div className="pricing-subtext">{t.pricing.card2Sub}</div>
               <ul className="pricing-features">
                 <li>✓ {t.pricing.c2_1}</li>
                 <li>✓ {t.pricing.c2_2}</li>
                 <li>✓ {t.pricing.c2_3}</li>
                 {/* <li>✓ {t.pricing.c2_4}</li> */}
               </ul>
               <button className="btn-primary-new" style={{width: "100%"}}>{t.pricing.btn2}</button>
               <p style={{fontSize: "0.8rem", color: "#666", marginTop: "15px"}}>{t.pricing.c2Footer}</p>
            </div>
         </div>
         <p style={{color: "white", marginTop: "40px", fontSize: "0.95rem"}}>{t.pricing.footerInfo}</p>
      </div>
      )}

      {/* FAQ */}
      <div id="faq" className="faq-section section-full">
         <div style={{maxWidth: "800px", margin: "0 auto"}}>
           <h2 style={{fontSize: "2.5rem", fontWeight: "bold", marginBottom: "30px", color: "#333"}}>{t.faq.title}</h2>
           
           <div className="faq-list">
             {[
               /* { q: t.faq.q1, a: t.faq.a1 }, */
               /* { q: t.faq.q2, a: t.faq.a2 }, */
               { q: t.faq.q3, a: t.faq.a3 },
               { q: t.faq.q4, a: t.faq.a4 },
               { q: t.faq.q5, a: t.faq.a5 }
             ].map((faq, index) => (
               <div 
                 key={index} 
                 className={`faq-item ${activeFaq === index ? 'active' : ''}`}
                 onClick={() => toggleFaq(index)}
               >
                 <div className="faq-question">
                   {faq.q}
                   <span>{activeFaq === index ? '✕' : '＋'}</span>
                 </div>
                 {activeFaq === index && (
                   <div className="faq-answer">
                     → {faq.a}
                   </div>
                 )}
               </div>
             ))}
           </div>
         </div>
      </div>

       {/* FOOTER */}
       <footer className="main-footer">
         <div className="section-container">
           <div className="footer-grid">
             <div className="footer-brand-side">
                <img src="/assets/img/landing-images/MiABC-01.png" alt="MiABC Logo" style={{ height: "45px" }} />
               <p>{t.nav.banner.split(".")[0]}.</p>
             </div>

             <div className="footer-col">
               <h4>Explora</h4>
               <ul className="footer-col-links">
                 <li><a href="#problem">{t.nav.problem}</a></li>
                 <li><a href="#how-it-works">{t.nav.howItWorks}</a></li>
                 <li><a href="#testimonials">{t.nav.testimonials}</a></li>
                 <li><a href="#schools">{language === "en" ? "Schools" : language === "ta" ? "பள்ளிகள்" : "Escuelas"}</a></li>
                 <li><a href="#faq">{t.nav.faq}</a></li>
                 <li><Link to="/user-guide">{t.footer.userGuide}</Link></li>
               </ul>
             </div>

              <div className="footer-col">
                <h4>Contacto</h4>
                <ul className="footer-col-links">
                  <li><a href="#contact"><strong>{t.nav.contact}</strong></a></li>
                  <li><a href="mailto:miabc.app@gmail.com"><strong>{t.footer.email}:</strong> miabc.app@gmail.com</a></li>
                 <li><a href="https://miabc.online" target="_blank" rel="noopener noreferrer"><strong>{t.footer.web}:</strong> miabc.online</a></li>
                 <li><Link to="/privacy"><strong>{t.footer.privacy}</strong></Link></li>
               </ul>
             </div>
           </div>

           <div className="footer-bottom">
             <div>
               DiazApps &copy; 2026. Todos los derechos reservados.
             </div>
             <div>
               {t.footer.developed}: <strong>DiazApps</strong>
             </div>
           </div>
         </div>
       </footer>

      {/* Floating Back to Top Button */}
      {showScrollTop && (
        <button 
          onClick={scrollToTop}
          className="floating-btn-top"
          aria-label="Volver arriba"
        >
          ↑
        </button>
      )}

    </div>
  )
}

export default Landing
